import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Zap, FileText, Share2, Mail, ShoppingBag } from 'lucide-react';

const GuidePage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <BookOpen className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-4xl font-bold text-gray-900 mb-4">使い方ガイド</h1>
        <p className="text-xl text-gray-600">ContentCraft AIを最大限活用する方法</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <h2 className="text-2xl font-bold mb-6">🚀 クイックスタート</h2>
        
        <div className="space-y-6">
          <div className="flex items-start">
            <div className="bg-blue-100 rounded-full p-2 mr-4 flex-shrink-0">
              <span className="text-blue-600 font-bold">1</span>
            </div>
            <div>
              <h3 className="font-semibold mb-2">アカウント登録</h3>
              <p className="text-gray-600">メールアドレスとパスワードで簡単登録。確認メールは不要です。</p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="bg-blue-100 rounded-full p-2 mr-4 flex-shrink-0">
              <span className="text-blue-600 font-bold">2</span>
            </div>
            <div>
              <h3 className="font-semibold mb-2">コンテンツタイプを選択</h3>
              <p className="text-gray-600">ブログ、SNS投稿、メール、商品説明から選べます。</p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="bg-blue-100 rounded-full p-2 mr-4 flex-shrink-0">
              <span className="text-blue-600 font-bold">3</span>
            </div>
            <div>
              <h3 className="font-semibold mb-2">プロンプトを入力</h3>
              <p className="text-gray-600">作成したい内容を簡潔に説明してください。</p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="bg-blue-100 rounded-full p-2 mr-4 flex-shrink-0">
              <span className="text-blue-600 font-bold">4</span>
            </div>
            <div>
              <h3 className="font-semibold mb-2">生成＆活用</h3>
              <p className="text-gray-600">生成されたコンテンツをコピーして、そのまま使えます。</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <FileText className="h-8 w-8 text-blue-600 mr-3" />
            <h3 className="text-xl font-bold">ブログ記事</h3>
          </div>
          <p className="text-gray-600 mb-4">
            SEOに最適化された1500〜2000文字の充実した記事を生成します。
          </p>
          <div className="bg-gray-50 rounded p-4">
            <p className="text-sm font-semibold mb-2">プロンプト例：</p>
            <p className="text-sm text-gray-600">
              「リモートワークでの生産性向上について、具体的なツールと方法を紹介」
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <Share2 className="h-8 w-8 text-blue-600 mr-3" />
            <h3 className="text-xl font-bold">SNS投稿</h3>
          </div>
          <p className="text-gray-600 mb-4">
            エンゲージメントを高める200〜300文字の投稿文を作成します。
          </p>
          <div className="bg-gray-50 rounded p-4">
            <p className="text-sm font-semibold mb-2">プロンプト例：</p>
            <p className="text-sm text-gray-600">
              「新商品のオーガニックコーヒー豆の発売告知、環境への配慮をアピール」
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <Mail className="h-8 w-8 text-blue-600 mr-3" />
            <h3 className="text-xl font-bold">ビジネスメール</h3>
          </div>
          <p className="text-gray-600 mb-4">
            件名から署名まで、完璧なビジネスメールを生成します。
          </p>
          <div className="bg-gray-50 rounded p-4">
            <p className="text-sm font-semibold mb-2">プロンプト例：</p>
            <p className="text-sm text-gray-600">
              「プロジェクトの進捗報告、来週の会議日程の調整依頼を含める」
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <ShoppingBag className="h-8 w-8 text-blue-600 mr-3" />
            <h3 className="text-xl font-bold">商品説明</h3>
          </div>
          <p className="text-gray-600 mb-4">
            魅力的で説得力のある商品説明文を作成します。
          </p>
          <div className="bg-gray-50 rounded p-4">
            <p className="text-sm font-semibold mb-2">プロンプト例：</p>
            <p className="text-sm text-gray-600">
              「防水性と通気性を両立したアウトドアジャケット、登山愛好家向け」
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg p-8 mb-8">
        <h2 className="text-2xl font-bold mb-4">💡 プロのコツ</h2>
        <ul className="space-y-3">
          <li className="flex items-start">
            <Zap className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong>具体的に書く：</strong>
              「ブログを書いて」より「初心者向けのPython入門、環境構築から基本文法まで」の方が良い結果が得られます。
            </div>
          </li>
          <li className="flex items-start">
            <Zap className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong>ターゲットを明確に：</strong>
              誰に向けた内容かを指定すると、より適切なトーンで生成されます。
            </div>
          </li>
          <li className="flex items-start">
            <Zap className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong>キーワードを含める：</strong>
              SEOを意識したキーワードを含めると、検索に強いコンテンツが生成されます。
            </div>
          </li>
          <li className="flex items-start">
            <Zap className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong>生成後の編集：</strong>
              生成されたコンテンツは下書きとして使い、あなたの個性を加えてください。
            </div>
          </li>
        </ul>
      </div>

      <div className="text-center">
        <Link
          to="/generate"
          className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition"
        >
          <Zap className="mr-2" />
          今すぐコンテンツを生成する
        </Link>
      </div>
    </div>
  );
};

export default GuidePage;